import type { CanvasClass } from '../types/creative-template.types';
import type { SceneGraph } from '../types/creative-template-example.types';
import type {
  DesignerCanvasClass,
  DesignerExemplar,
  DesignerExemplarCoverage,
  DesignerExemplarCoverageClass,
  DesignerExemplarSignedUrlResponse,
  DesignerExemplarWritePayload,
  ExtractedStructure,
} from '../types/designer-exemplar.types';

export function buildDesignerExemplarPayload(
  values: DesignerExemplarWritePayload
): DesignerExemplarWritePayload {
  return {
    ...values,
    name: values.name?.trim(),
    vertical: values.vertical?.trim(),
    notes: values.notes?.trim(),
  };
}

export function validateDesignerExemplarForm(
  values: DesignerExemplarWritePayload
): string | null {
  if (!values.name?.trim()) {
    return 'Name is required.';
  }
  if (!values.canvas_class) {
    return 'Canvas class is required.';
  }
  if (!values.vertical?.trim()) {
    return 'Vertical is required.';
  }
  return null;
}

export function parseDesignerExemplarMutationError(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return 'Could not save exemplar. You may need staff or admin access.';
  }
  const data = (error as { data?: Record<string, unknown> }).data;
  if (!data) {
    return 'Could not save exemplar.';
  }
  if (typeof data.detail === 'string') {
    return data.detail;
  }
  const messages = Object.entries(data).map(([field, value]) => {
    if (Array.isArray(value)) {
      return `${field}: ${value.join(', ')}`;
    }
    return `${field}: ${String(value)}`;
  });
  return messages.length > 0 ? messages.join(' ') : 'Could not save exemplar.';
}

export function asList<T>(value: unknown): T[] {
  if (Array.isArray(value)) {
    return value;
  }
  if (!value || typeof value !== 'object') {
    return [];
  }
  const record = value as Record<string, unknown>;
  if (Array.isArray(record.items)) {
    return record.items as T[];
  }
  if (Array.isArray(record.results)) {
    return record.results as T[];
  }
  if (Array.isArray(record.item)) {
    return record.item as T[];
  }
  return [];
}

export function unwrapDesignerApiItem<T>(response: unknown): T | undefined {
  if (response && typeof response === 'object' && 'item' in response) {
    return (response as { item: T }).item;
  }
  return response as T | undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return undefined;
}

export function isDisplayableImageUrl(url: string | null | undefined): url is string {
  if (!url) {
    return false;
  }
  return /^(https?:|blob:|data:)/i.test(url);
}

export function fileExtensionForUpload(file: File): string {
  const fromName = file.name.includes('.') ? file.name.split('.').pop() : '';
  if (fromName?.trim()) {
    return fromName.replace(/^\./, '').toLowerCase();
  }
  const typeMap: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
  };
  return typeMap[file.type] ?? 'png';
}

export function normaliseDesignerExemplar(value: unknown): DesignerExemplar | null {
  const record = asRecord(unwrapDesignerApiItem(value) ?? value);
  if (!record) {
    return null;
  }
  const id = firstString(record.id, record.exemplar_id);
  if (!id) {
    return null;
  }
  const ratioMatches = record.ratio_matches_class;
  const ratioMismatch = typeof record.ratio_mismatch === 'boolean'
    ? record.ratio_mismatch
    : ratioMatches === false;
  return {
    ...(record as unknown as DesignerExemplar),
    id,
    name: firstString(record.name) ?? '',
    canvas_class: (firstString(record.canvas_class) ?? 'panel_square') as DesignerExemplar['canvas_class'],
    vertical: firstString(record.vertical) ?? '',
    notes: firstString(record.notes) ?? '',
    active: record.active !== false,
    extraction_status: (firstString(record.extraction_status) ?? 'pending') as DesignerExemplar['extraction_status'],
    extracted_structure: (record.extracted_structure ?? record.scene_graph ?? null) as ExtractedStructure | null,
    ratio_mismatch: Boolean(ratioMismatch),
    image_url: firstString(record.image_url, record.source_image_url) ?? null,
    image_width: typeof record.image_width === 'number' ? record.image_width : null,
    image_height: typeof record.image_height === 'number' ? record.image_height : null,
    created_at: firstString(record.created_at) ?? '',
    updated_at: firstString(record.updated_at) ?? '',
  };
}

export function applyDesignerExtractEnqueue(
  current: DesignerExemplar | null,
  enqueue: unknown
): DesignerExemplar | null {
  if (!current) {
    return current;
  }
  const record = asRecord(unwrapDesignerApiItem(enqueue) ?? enqueue);
  const id = firstString(record?.id, record?.exemplar_id, current.id);
  if (!id) {
    return current;
  }
  return {
    ...current,
    id,
    extraction_status: (
      firstString(record?.extraction_status) ?? 'pending'
    ) as DesignerExemplar['extraction_status'],
  };
}

export function mergeDesignerCommitResult(
  current: DesignerExemplar | null,
  commit: unknown
): DesignerExemplar | null {
  const record = asRecord(unwrapDesignerApiItem(commit) ?? commit);
  const id = firstString(record?.id, current?.id);
  if (!id) {
    return current;
  }
  const imageUrl = firstString(record?.image_url, record?.source_image_url, current?.image_url) ?? null;
  const ratioMatches = record?.ratio_matches_class;
  const ratioMismatch = typeof record?.ratio_mismatch === 'boolean'
    ? record.ratio_mismatch
    : typeof ratioMatches === 'boolean'
      ? !ratioMatches
      : Boolean(current?.ratio_mismatch);
  const base = current ?? normaliseDesignerExemplar({ ...record, id });
  if (!base) {
    return null;
  }
  return {
    ...base,
    id,
    image_url: imageUrl,
    image_width: typeof record?.image_width === 'number' ? record.image_width : base.image_width,
    image_height: typeof record?.image_height === 'number' ? record.image_height : base.image_height,
    ratio_mismatch: ratioMismatch,
    extraction_status: (
      firstString(record?.extraction_status) ?? base.extraction_status
    ) as DesignerExemplar['extraction_status'],
  };
}

export function normaliseDesignerSignedUrl(value: unknown): DesignerExemplarSignedUrlResponse {
  const record = asRecord(unwrapDesignerApiItem(value) ?? value) ?? {};
  const signedUrl = firstString(record.upload_url, record.signed_url, record.url);
  const objectName = firstString(record.object_path, record.object_name, record.key);
  if (!signedUrl || !objectName) {
    throw new Error('Studio did not return an upload URL. The image was not uploaded.');
  }
  return {
    signed_url: signedUrl,
    object_name: objectName,
    gcs_uri: firstString(record.gcs_uri),
  };
}

export function normaliseDesignerExemplars(value: unknown): DesignerExemplar[] {
  return asList<unknown>(value)
    .map((entry) => normaliseDesignerExemplar(entry))
    .filter((entry): entry is DesignerExemplar => Boolean(entry));
}

export function normaliseDesignerCanvasClasses(value: unknown): DesignerCanvasClass[] {
  return asList<DesignerCanvasClass & { ratio_tolerance?: number }>(value)
    .filter((entry) => Boolean(entry && typeof entry === 'object' && entry.slug))
    .map((entry) => ({
      ...entry,
      tolerance: Number(entry.tolerance ?? entry.ratio_tolerance ?? 0) || 0,
    }));
}

export function normaliseDesignerCoverage(value: unknown): DesignerExemplarCoverage {
  if (Array.isArray(value)) {
    return {
      target_per_class: 0,
      classes: normaliseCoverageClasses(value),
    };
  }
  if (!value || typeof value !== 'object') {
    return { target_per_class: 0, classes: [] };
  }
  const record = value as Record<string, unknown>;
  const classes = normaliseCoverageClasses(
    record.classes ?? record.per_class ?? record.items ?? record.results ?? record.coverage
  );
  const target = Number(record.target_per_class ?? record.target ?? 0);
  return {
    target_per_class: Number.isFinite(target) ? target : 0,
    classes,
  };
}

function normaliseCoverageClasses(value: unknown): DesignerExemplarCoverageClass[] {
  return asList<DesignerExemplarCoverageClass & { slug?: string; thin_class?: boolean }>(value).map((entry) => {
    const canvasClass = (entry.canvas_class ?? entry.slug ?? '') as CanvasClass;
    return {
      canvas_class: canvasClass,
      display_name: entry.display_name,
      count: Number(entry.count ?? 0) || 0,
      active_count: Number(entry.active_count ?? entry.count ?? 0) || 0,
      is_thin: Boolean(entry.is_thin ?? entry.thin_class),
    };
  });
}

export function canvasClassLabel(
  canvasClass: string | null | undefined,
  options?: Array<{ slug: string; display_name: string }>
): string {
  const slug = typeof canvasClass === 'string' ? canvasClass : '';
  const match = options?.find((option) => option.slug === slug);
  if (match?.display_name) {
    return match.display_name;
  }
  return slug ? slug.replace(/_/g, ' ') : 'Unknown';
}

export function isSceneGraph(structure: ExtractedStructure | null | undefined): structure is SceneGraph {
  return Boolean(structure?.canvas_class && Array.isArray(structure.layers));
}

export function formatExtractedStructureSummary(structure: ExtractedStructure | null): string {
  if (!structure) {
    return 'No extracted structure yet.';
  }
  const lines: string[] = [];
  if (structure.layout_archetype) {
    lines.push(`Layout archetype: ${structure.layout_archetype}`);
  }
  const elementCounts = structure.element_counts ?? {
    ...(typeof structure.text_element_count === 'number' ? { text: structure.text_element_count } : {}),
    ...(typeof structure.shape_layer_count === 'number' ? { shape: structure.shape_layer_count } : {}),
    ...(typeof structure.color_role_count === 'number' ? { colour_roles: structure.color_role_count } : {}),
  };
  if (Object.keys(elementCounts).length > 0) {
    const counts = Object.entries(elementCounts)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
    lines.push(`Element counts: ${counts}`);
  }
  if (structure.type_scale_ratios && Object.keys(structure.type_scale_ratios).length > 0) {
    const ratios = Object.entries(structure.type_scale_ratios)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
    lines.push(`Type scale ratios: ${ratios}`);
  }
  const margin = structure.margin_fraction ?? structure.margin_fraction_of_short_edge;
  if (margin != null) {
    lines.push(`Margin fraction: ${margin}`);
  }
  if (structure.hero_treatment) {
    lines.push(`Hero treatment: ${structure.hero_treatment}`);
  }
  if (structure.dominant_alignment) {
    lines.push(`Dominant alignment: ${structure.dominant_alignment}`);
  }
  if (typeof structure.text_over_image === 'boolean') {
    lines.push(`Text over image: ${structure.text_over_image ? 'yes' : 'no'}`);
  }
  if (structure.palette_hexes?.length) {
    lines.push(`Palette: ${structure.palette_hexes.join(', ')}`);
  }
  return lines.length > 0 ? lines.join('\n') : 'Extracted structure present but empty.';
}

function parseUploadErrorBody(body: string, status: number): string {
  if (body.includes('<Code>')) {
    const codeMatch = body.match(/<Code>([^<]+)<\/Code>/);
    const messageMatch = body.match(/<Message>([^<]+)<\/Message>/);
    if (codeMatch && messageMatch) {
      return `${codeMatch[1]}: ${messageMatch[1]}`;
    }
  }
  return body || `Upload failed with status ${status}`;
}

export async function uploadFileToSignedUrl(
  file: File,
  signedUrl: string,
  onProgress?: (percent: number) => void,
  headers?: Record<string, string>
): Promise<void> {
  if (!signedUrl) {
    throw new Error('Invalid signed URL received from server');
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded * 100) / event.total));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(parseUploadErrorBody(xhr.responseText, xhr.status)));
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload. The file may not have reached storage.'));
    });

    xhr.open('PUT', signedUrl);
    const contentType = headers?.['Content-Type'] ?? file.type ?? 'application/octet-stream';
    xhr.setRequestHeader('Content-Type', contentType);
    if (headers) {
      Object.entries(headers).forEach(([key, value]) => {
        if (key.toLowerCase() !== 'content-type') {
          xhr.setRequestHeader(key, value);
        }
      });
    }
    xhr.send(file);
  });
}
